import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFirebaseImage } from '../Helpers/ImageLoader';

interface ImagePreviewProps {
    /** Storage path for Firebase image (optional - provide either this or imageUrl) */
    storagePath?: string | null;
    /** Direct URL for image (optional - provide either this or storagePath) */
    imageUrl?: string;
    /** Alt text for the image */
    alt?: string;
}

/**
 * Image preview component with zoom and pan functionality.
 * Supports both Firebase storage paths and direct image URLs.
 *
 * Controls:
 * - Mouse wheel: Zoom in/out
 * - Click and drag: Pan around the image
 */
export const ImagePreview: React.FC<ImagePreviewProps> = ({
    storagePath,
    imageUrl,
    alt = 'Förhandsvisning'
}) => {
    const firebaseImage = useFirebaseImage(storagePath || null);
    const src = storagePath ? firebaseImage.src : imageUrl;
    const isLoading = storagePath ? firebaseImage.isLoading : false;
    const hasError = storagePath ? firebaseImage.hasError : false;

    // Zoom and pan state
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);

    // Reset zoom and pan when image changes
    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [src]);

    // Handle mouse wheel zoom
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.max(0.1, Math.min(5, zoom * delta));
        setZoom(newZoom);
    }, [zoom]);

    // Handle mouse down to start panning
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }, [pan]);

    // Handle mouse move to pan
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        setPan({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    }, [isDragging, dragStart]);

    // Handle mouse up to stop panning
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Handle mouse leave to stop panning
    const handleMouseLeave = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add global mouse move and up listeners when dragging
    useEffect(() => {
        if (isDragging) {
            const handleGlobalMouseMove = (e: MouseEvent) => {
                setPan({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y
                });
            };

            const handleGlobalMouseUp = () => {
                setIsDragging(false);
            };

            window.addEventListener('mousemove', handleGlobalMouseMove);
            window.addEventListener('mouseup', handleGlobalMouseUp);

            return () => {
                window.removeEventListener('mousemove', handleGlobalMouseMove);
                window.removeEventListener('mouseup', handleGlobalMouseUp);
            };
        }
    }, [isDragging, dragStart]);

    if (isLoading) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100">
                <div className="text-center text-muted">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <p className="mt-2">Laddar bild...</p>
                </div>
            </div>
        );
    }

    if (hasError || !src) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100">
                <div className="text-center text-muted">
                    <p>Kunde inte ladda bilden</p>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="h-100 overflow-hidden"
            style={{
                backgroundColor: '#f5f5f5',
                cursor: isDragging ? 'grabbing' : 'grab',
                position: 'relative'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onWheel={handleWheel}
        >
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: 'center center',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                }}
            >
                <img
                    src={src}
                    alt={alt}
                    style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        userSelect: 'none',
                        pointerEvents: 'none'
                    }}
                    draggable={false}
                />
            </div>
        </div>
    );
};

/**
 * Helper to check if a file type is an image
 */
export const isImageFileType = (fileType: string | undefined): boolean => {
    if (!fileType) return false;
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    return imageExtensions.includes(fileType.toLowerCase());
};
